// src/constants/defaultEditorState.ts
var defaultEditorState = {
  content: { type: "doc" },
  position: { from: 0, to: 0 }
};

// src/enums/TextContentType.ts
var TextContentType = /* @__PURE__ */ ((TextContentType2) => {
  TextContentType2[TextContentType2["PARAGRAPH"] = 0] = "PARAGRAPH";
  TextContentType2[TextContentType2["HEADING1"] = 1] = "HEADING1";
  TextContentType2[TextContentType2["HEADING2"] = 2] = "HEADING2";
  TextContentType2[TextContentType2["HEADING3"] = 3] = "HEADING3";
  TextContentType2[TextContentType2["HEADING4"] = 4] = "HEADING4";
  TextContentType2[TextContentType2["HEADING5"] = 5] = "HEADING5";
  TextContentType2[TextContentType2["HEADING6"] = 6] = "HEADING6";
  return TextContentType2;
})(TextContentType || {});

// src/extensions/bubbleMenu.ts
import {
  posToDOMRect,
  isTextSelection,
  isNodeSelection
} from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { findParentNodeOfType } from "prosemirror-utils";
import tippy from "tippy.js";
var BubbleMenuInstance = class {
  editor;
  element;
  view;
  preventHide = false;
  tippy;
  type = "default";
  constructor({
    editor,
    element,
    view,
    tippyOptions,
    type
  }) {
    this.editor = editor;
    this.element = element;
    this.view = view;
    this.element.addEventListener("mousedown", this.mousedownHandler, { capture: true });
    this.view.dom.addEventListener("dragstart", this.dragstartHandler);
    this.editor.on("focus", this.focusHandler);
    this.editor.on("blur", this.blurHandler);
    this.createTooltip(tippyOptions);
    this.element.style.visibility = "visible";
    this.type = type ?? "default";
  }
  mousedownHandler = () => {
    this.preventHide = true;
  };
  dragstartHandler = () => {
    this.hide();
  };
  focusHandler = () => {
    setTimeout(() => {
      this.update(this.editor.view);
    });
  };
  blurHandler = ({ event }) => {
    if (this.preventHide) {
      this.preventHide = false;
      return;
    }
    if (event?.relatedTarget && this.element.parentNode?.contains(event.relatedTarget)) {
      return;
    }
    this.hide();
  };
  createTooltip(options = {}) {
    this.tippy = tippy(this.view.dom, {
      duration: 0,
      getReferenceClientRect: null,
      content: this.element,
      interactive: true,
      trigger: "manual",
      placement: "top",
      hideOnClick: "toggle",
      ...options
    });
  }
  update(view, oldState) {
    const { state, composing } = view;
    const { doc, selection } = state;
    const isSame = oldState?.doc.eq(doc) && oldState.selection.eq(selection);
    if (composing || isSame) {
      return;
    }
    const { empty, ranges } = selection;
    const from = Math.min(...ranges.map((range) => range.$from.pos));
    const to = Math.max(...ranges.map((range) => range.$to.pos));
    const isEmptyTextBlock = !doc.textBetween(from, to).length && isTextSelection(view.state.selection);
    if (this.type === "default" && (empty || isEmptyTextBlock)) {
      this.hide();
      return;
    }
    if (this.type === "table" && !this.editor.isActive("table")) {
      this.hide();
      return;
    }
    this.tippy.setProps({
      getReferenceClientRect: () => {
        if (this.type === "table") {
          const tableNode = findParentNodeOfType(this.editor.schema.nodes.table)(view.state.selection);
          return posToDOMRect(view, tableNode?.pos ?? from, tableNode?.pos ?? to);
        }
        if (isNodeSelection(view.state.selection)) {
          const node = view.nodeDOM(from);
          if (node) {
            return node.getBoundingClientRect();
          }
        }
        return posToDOMRect(view, from, to);
      }
    });
    this.show();
  }
  show() {
    this.tippy.show();
  }
  hide() {
    this.tippy.hide();
  }
  destroy() {
    this.tippy.destroy();
    this.element.removeEventListener("mousedown", this.mousedownHandler);
    this.view.dom.removeEventListener("dragstart", this.dragstartHandler);
    this.editor.off("focus", this.focusHandler);
    this.editor.off("blur", this.blurHandler);
  }
};
var BubbleMenuPluginKey = new PluginKey("menuBubble");
var BubbleMenuPlugin = (options) => {
  return new Plugin({
    key: BubbleMenuPluginKey,
    view: (view) => new BubbleMenuInstance({ view, ...options })
  });
};
var createBubbleMenu = (core) => core.Extension.create({
  name: "bubbleMenu",
  defaultOptions: {
    element: null,
    tippyOptions: {},
    type: "default"
  },
  addProseMirrorPlugins() {
    if (!this.options.element) {
      return [];
    }
    return [
      BubbleMenuPlugin({
        editor: this.editor,
        element: this.options.element,
        tippyOptions: this.options.tippyOptions,
        type: this.options.type
      })
    ];
  }
});

// src/extensions/heading.ts
import { mergeAttributes } from "@tiptap/core";
var createHeading = (core) => core.Node.create({
  name: "heading",
  defaultOptions: {
    levels: [1, 2, 3, 4, 5, 6],
    HTMLAttributes: {}
  },
  content: "inline*",
  group: "block",
  defining: true,
  renderHTML({ node, HTMLAttributes }) {
    const hasLevel = this.options.levels.includes(node.attrs.level);
    const level = hasLevel ? node.attrs.level : this.options.levels[0];
    if (level === 2) {
      return [
        "h2",
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
        [
          "span",
          {
            class: "title"
          },
          node.textContent
        ]
      ];
    } else {
      return [`h${level}`, mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    }
  }
});

// src/extensions/textColor.ts
import "@tiptap/extension-text-style";
var createTextColor = (core) => core.Extension.create({
  name: "textColor",
  addOptions() {
    return {
      types: ["textStyle"]
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textColor: {
            default: null,
            parseHTML: (element) => element.style.color?.replace(/['"]+/g, ""),
            renderHTML: (attributes) => {
              if (!attributes.textColor) {
                return {};
              }
              return {
                style: `color: ${attributes.textColor}`
              };
            }
          }
        }
      }
    ];
  },
  addCommands() {
    return {
      setTextColor: (textColor) => ({ chain }) => {
        return chain().setMark("textStyle", { textColor }).run();
      },
      unsetTextColor: () => ({ chain }) => {
        return chain().setMark("textStyle", { textColor: null }).removeEmptyTextStyle().run();
      }
    };
  }
});

// src/utils/getCore.ts
var getCore = async () => await import("@tiptap/core");

// src/extensions/section.ts
var createSection = (core) => core.Node.create({
  name: "section",
  defaultOptions: {
    HTMLAttributes: {}
  },
  content: "block*",
  group: "block",
  selectable: true,
  defining: true,
  isolating: true,
  exitable: true,
  inclusive: false,
  renderHTML({ node, HTMLAttributes }) {
    return ["section", HTMLAttributes, 0];
  }
});

// src/extensions/effectiveRule.ts
import { mergeAttributes as mergeAttributes2 } from "@tiptap/core";
var createEffectiveRule = (core) => core.Node.create({
  name: "effectiveRule",
  group: "block",
  content: "inline*",
  atom: true,
  renderHTML({ HTMLAttributes }) {
    return [
      "details",
      mergeAttributes2(HTMLAttributes, { class: "effective-rule" }),
      [
        "summary",
        { class: "exclusion-summary" },
        "\u6392\u9664\u4F4E\u8D54\u7387\u3001\u63D0\u524D\u5151\u73B0\u3001\u7279\u6B8A\u6295\u6CE8\u3001\u5F69\u7968"
      ],
      [
        "ul",
        { class: "exclusion-list" },
        [
          "li",
          {},
          "\u6392\u9664",
          ["strong", {}, " \u4F53\u80B2/\u7535\u7ADE "],
          "\u6B27\u76D81.70\u3001\u6E2F\u76D80.70\u3001\u6B63\u6570\u9A6C\u6765\u76D80.70\u3001\u5370\u5C3C\u76D8-1.43"
        ],
        [
          "li",
          {},
          "\u6392\u9664",
          ["strong", {}, " \u4F53\u80B2/\u7535\u7ADE "],
          "\u63D0\u524D\u5151\u73B0\u3001\u7279\u6B8A\u6295\u6CE8"
        ],
        [
          "li",
          {},
          "\u6392\u9664",
          ["strong", {}, " \u5F69\u7968 "],
          "\u6240\u6709\u573A\u9986"
        ]
      ]
    ];
  }
});

// src/utils/getExtensions.ts
var getRenderExtensions = async (core) => {
  try {
    return [
      (await import("@tiptap/starter-kit")).default,
      (await import("@tiptap/extension-text-style")).TextStyle,
      (await import("@tiptap/extension-table")).Table,
      (await import("@tiptap/extension-table-cell")).TableCell,
      (await import("@tiptap/extension-table-header")).TableHeader,
      (await import("@tiptap/extension-table-row")).TableRow,
      (await import("@tiptap/extension-link")).Link.extend({ inclusive: false }).configure({ openOnClick: false, HTMLAttributes: { class: "tiptap-extension-link prevent-router" } }),
      createSection(core),
      createEffectiveRule(core),
      createTextColor(core)
    ];
  } catch {
    return [];
  }
};
var getCampaignExtensions = async (core) => {
  try {
    return [
      ...await getRenderExtensions(core),
      createHeading(core)
    ];
  } catch {
    return [];
  }
};
var getEditorExtensions = async (core, placeholderConfigs) => {
  try {
    return [
      ...await getRenderExtensions(core),
      createBubbleMenu(core),
      (await import("@tiptap/extension-placeholder")).Placeholder.configure({
        placeholder: placeholderConfigs.placeholder,
        emptyEditorClass: placeholderConfigs.emptyEditorClass
      })
    ];
  } catch {
    return [];
  }
};

// src/utils/handlePlainTextPaste.ts
import { Fragment, Slice } from "prosemirror-model";
var handlePlainTextPaste = (view, event) => {
  const clipboardData = event.clipboardData;
  if (!clipboardData) return true;
  const text = clipboardData.getData("text/plain");
  if (text === "") return true;
  const { schema } = view.state;
  const hardBreak = schema.nodes.hardBreak;
  const nodes = text.replace(/\r\n?/g, "\n").split("\n").flatMap((line, index) => [
    ...index > 0 ? [hardBreak.create()] : [],
    ...line === "" ? [] : [schema.text(line)]
  ]);
  const slice = new Slice(Fragment.fromArray(nodes), 0, 0);
  const transaction = view.state.tr.replaceSelection(slice).scrollIntoView().setMeta("paste", true).setMeta("uiEvent", "paste");
  view.dispatch(transaction);
  return true;
};

// src/utils/insertText.ts
import { Editor as Editor2 } from "@tiptap/core";
var insertText = async (state, text) => {
  const core = await getCore();
  const extensions = await getRenderExtensions(core);
  const coreEditor = new Editor2({
    extensions,
    content: state.content
  });
  coreEditor.commands.insertContentAt(state.position, text);
  return {
    content: coreEditor.getJSON(),
    position: { from: coreEditor.state.selection.from, to: coreEditor.state.selection.to }
  };
};

// src/utils/isEmpty.ts
var isEmpty = (json) => !json.content || json.content?.length === 1 && !json.content[0].content;

// src/utils/parseContent.ts
var parseContent = async (data) => {
  const core = await getCore();
  const extensions = await getRenderExtensions(core);
  try {
    return JSON.parse(data);
  } catch {
    return core.generateJSON(data, extensions);
  }
};

// src/utils/renderCampaign.ts
var renderCampaign = async (data) => {
  const core = await getCore();
  const extensions = await getCampaignExtensions(core);
  try {
    return core.generateHTML(JSON.parse(data), extensions);
  } catch (e) {
    return data;
  }
};

// src/utils/renderContent.ts
var renderContent = async (data) => {
  const core = await getCore();
  const extensions = await getRenderExtensions(core);
  try {
    return core.generateHTML(JSON.parse(data), extensions);
  } catch (e) {
    return data;
  }
};

// src/utils/serializePlainTextClipboard.ts
var serializePlainTextClipboard = (slice) => slice.content.textBetween(
  0,
  slice.content.size,
  "\n",
  (node) => node.type.name === "hardBreak" ? "\n" : ""
);

// src/utils/selectTextLineOnTripleClick.ts
import { TextSelection } from "prosemirror-state";
var selectTextLineOnTripleClick = (view, position) => {
  const { state } = view;
  const resolvedPosition = state.doc.resolve(position);
  let depth = resolvedPosition.depth;
  while (depth > 0 && !resolvedPosition.node(depth).isTextblock) depth--;
  if (depth === 0) return false;
  const textBlock = resolvedPosition.node(depth);
  const textBlockStart = resolvedPosition.start(depth);
  let from = textBlockStart;
  let to = textBlockStart + textBlock.content.size;
  textBlock.forEach((node, offset) => {
    if (node.type.name !== "hardBreak") return;
    const breakPosition = textBlockStart + offset;
    if (breakPosition < position) {
      from = breakPosition + node.nodeSize;
    } else {
      to = Math.min(to, breakPosition);
    }
  });
  view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, from, to)));
  return true;
};

// src/utils/trimEndContent.ts
var trimParagraphEnd = (paragraph) => {
  const content = [...paragraph.content ?? []];
  while (content.length > 0) {
    const lastIndex = content.length - 1;
    const lastNode = content[lastIndex];
    if (lastNode.type === "hardBreak") {
      content.pop();
      continue;
    }
    if (lastNode.type !== "text") break;
    const text = lastNode.text?.trimEnd() ?? "";
    if (text === "") {
      content.pop();
      continue;
    }
    content[lastIndex] = { ...lastNode, text };
    break;
  }
  return {
    ...paragraph,
    content: content.length > 0 ? content : void 0
  };
};
var trimEndContent = (document) => {
  const content = [...document.content ?? []];
  while (content.length > 0) {
    const lastIndex = content.length - 1;
    const lastNode = content[lastIndex];
    if (lastNode.type !== "paragraph") break;
    const paragraph = trimParagraphEnd(lastNode);
    if (paragraph.content) {
      content[lastIndex] = paragraph;
      break;
    }
    content.pop();
  }
  return {
    ...document,
    content: content.length > 0 ? content : void 0
  };
};

// src/utils/undoHistory.ts
import { closeHistory, undo, undoDepth } from "prosemirror-history";
var getUndoDepth = (view) => {
  const depth = undoDepth(view.state);
  return typeof depth === "number" ? depth : 0;
};
var startHistoryGroup = (view) => {
  view.dispatch(closeHistory(view.state.tr));
  return getUndoDepth(view);
};
var undoHistory = (view, minimumDepth = 0) => {
  if (getUndoDepth(view) <= minimumDepth) return false;
  return undo(view.state, (transaction) => {
    view.dispatch(transaction);
  });
};

// src/index.ts
import { Editor as Editor3 } from "@tiptap/core";
export * from "tippy.js";
export {
  BubbleMenuPlugin,
  BubbleMenuPluginKey,
  Editor3 as Editor,
  TextContentType,
  createBubbleMenu,
  createHeading,
  createTextColor,
  defaultEditorState,
  getCampaignExtensions,
  getCore,
  getEditorExtensions,
  getRenderExtensions,
  handlePlainTextPaste,
  insertText,
  isEmpty,
  parseContent,
  renderCampaign,
  renderContent,
  selectTextLineOnTripleClick,
  serializePlainTextClipboard,
  startHistoryGroup,
  trimEndContent,
  undoHistory
};
//# sourceMappingURL=index.js.map